/**
 * Notification Bell Component
 *
 * Displays a bell icon with unread badge count.
 * Merges server-side notifications (from RTDB) with client-computed ones
 * (due reviews, streak risk). Real-time listener on notifications/{uid}.
 * Supports read/unread state and "Mark all read".
 */

import { useState, useEffect, useRef, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ref as dbRef, get } from 'firebase/database'
import { db } from '@/lib/firebase'
import { ReviewSchedule, Lesson } from '@/types/models'
import { getDueReviews } from '@/services/progressService'
import { isStreakAtRisk } from '@/services/achievementService'
import {
  subscribeNotifications,
  markNotificationRead,
  markAllRead,
  type Notification as ServerNotification,
} from '@/services/notificationService'

interface NotificationBellProps {
  uid: string
}

interface NotificationItem {
  id: string
  type: 'review_due' | 'streak_at_risk' | 'review_reminder' | 'achievement_earned' | 'general'
  title: string
  message: string
  lessonId?: string
  urgency: 'high' | 'medium' | 'low'
  read: boolean
  source: 'server' | 'client'
}

export function NotificationBell({ uid }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [clientLoaded, setClientLoaded] = useState(false)
  const [serverLoaded, setServerLoaded] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Client-computed notifications (reviews, streak)
  const [clientItems, setClientItems] = useState<NotificationItem[]>([])
  // Server notifications from RTDB
  const [serverItems, setServerItems] = useState<NotificationItem[]>([])

  const loading = !clientLoaded && !serverLoaded

  // Subscribe to server notifications (real-time)
  useEffect(() => {
    const unsubscribe = subscribeNotifications(uid, (serverNotifs: ServerNotification[]) => {
      const mapped: NotificationItem[] = serverNotifs.map(n => ({
        id: n.id,
        type: n.type as NotificationItem['type'],
        title: n.title,
        message: n.message,
        lessonId: n.lessonId,
        urgency: 'medium' as const,
        read: n.read,
        source: 'server' as const,
      }))
      setServerItems(mapped)
      setServerLoaded(true)
    })

    return unsubscribe
  }, [uid])

  // Load client-computed notifications on mount + refresh every 5 min
  useEffect(() => {
    let cancelled = false

    const loadClientNotifications = async () => {
      try {
        const items: NotificationItem[] = []

        const [dueReviews, lessonsSnapshot, streakSnapshot] = await Promise.all([
          getDueReviews(uid),
          get(dbRef(db, 'lessons')),
          get(dbRef(db, `streaks/${uid}`)),
        ])

        const lessons: Record<string, Lesson> = lessonsSnapshot.exists()
          ? lessonsSnapshot.val()
          : {}

        dueReviews.forEach((review: ReviewSchedule) => {
          const lesson = lessons[review.lessonId]
          const hoursOverdue = (Date.now() - review.nextReviewDate) / (1000 * 60 * 60)

          items.push({
            id: `review-${review.lessonId}`,
            type: 'review_due',
            title: lesson?.title || 'Review Due',
            message: hoursOverdue > 0
              ? `Overdue by ${Math.floor(hoursOverdue / 24)} day${Math.floor(hoursOverdue / 24) !== 1 ? 's' : ''}`
              : 'Due today',
            lessonId: review.lessonId,
            urgency: hoursOverdue > 48 ? 'high' : hoursOverdue > 0 ? 'medium' : 'low',
            read: false,
            source: 'client',
          })
        })

        if (streakSnapshot.exists()) {
          const streak = streakSnapshot.val()
          if (isStreakAtRisk(streak) && streak.currentStreak > 1) {
            items.push({
              id: 'streak-risk',
              type: 'streak_at_risk',
              title: 'Streak at Risk!',
              message: `Complete a lesson to keep your ${streak.currentStreak}-day streak`,
              urgency: 'high',
              read: false,
              source: 'client',
            })
          }
        }

        if (!cancelled) {
          setClientItems(items)
          setClientLoaded(true)
        }
      } catch (err) {
        console.error('Error loading client notifications:', err)
        if (!cancelled) setClientLoaded(true)
      }
    }

    loadClientNotifications()
    const interval = setInterval(loadClientNotifications, 5 * 60 * 1000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [uid])

  // Merge server + client notifications, de-duplicate by id
  const notifications = useMemo(() => {
    const merged = new Map<string, NotificationItem>()

    // Server items first (they have read/unread state)
    serverItems.forEach(item => merged.set(item.id, item))
    // Client items fill in what server doesn't cover
    clientItems.forEach(item => {
      if (!merged.has(item.id)) {
        merged.set(item.id, item)
      }
    })

    const all = Array.from(merged.values())

    // Sort: unread first, then by urgency
    all.sort((a, b) => {
      if (a.read !== b.read) return a.read ? 1 : -1
      const order = { high: 0, medium: 1, low: 2 }
      return order[a.urgency] - order[b.urgency]
    })

    return all
  }, [serverItems, clientItems])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleMarkRead = async (notificationId: string) => {
    try {
      await markNotificationRead(uid, notificationId)
    } catch {
      // Server items will auto-update via real-time subscription
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await markAllRead(uid)
    } catch (err) {
      console.error('Error marking all read:', err)
    }
  }

  const unreadCount = notifications.filter(n => !n.read).length
  const hasHighPriority = notifications.some(n => n.urgency === 'high' && !n.read)

  const getUrgencyStyles = (urgency: string, read: boolean) => {
    if (read) return 'border-l-4 border-l-gray-300 bg-gray-50'
    switch (urgency) {
      case 'high':
        return 'border-l-4 border-l-red-500 bg-red-50'
      case 'medium':
        return 'border-l-4 border-l-amber-500 bg-amber-50'
      default:
        return 'border-l-4 border-l-blue-500 bg-blue-50'
    }
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'review_due':
      case 'review_reminder':
        return '📚'
      case 'streak_at_risk':
      case 'streak_risk':
        return '🔥'
      case 'achievement_earned':
        return '🏆'
      default:
        return '🔔'
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <svg
          className={`w-6 h-6 ${hasHighPriority ? 'text-red-600' : 'text-gray-600'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {unreadCount > 0 && (
          <span
            className={`absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center text-xs font-bold text-white rounded-full px-1 ${
              hasHighPriority ? 'bg-red-600 animate-pulse' : 'bg-amber-500'
            }`}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center">
                <div className="text-2xl mb-1">✅</div>
                <p className="text-gray-600 text-sm font-medium">All caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map(notification => (
                  <div key={notification.id}>
                    {notification.lessonId ? (
                      <Link
                        to={`/lesson/${notification.lessonId}`}
                        onClick={() => {
                          setIsOpen(false)
                          if (notification.source === 'server' && !notification.read) {
                            handleMarkRead(notification.id)
                          }
                        }}
                        className={`block px-3 py-2 hover:bg-gray-50 transition-colors ${getUrgencyStyles(notification.urgency, notification.read)}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-base flex-shrink-0">{getIcon(notification.type)}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-medium leading-tight ${notification.read ? 'text-gray-500' : 'text-gray-900'}`}>
                              {notification.title}
                            </p>
                            <p className="text-xs text-gray-500">{notification.message}</p>
                          </div>
                          {!notification.read && (
                            <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                          )}
                          <span className="text-gray-400 text-xs flex-shrink-0">&rarr;</span>
                        </div>
                      </Link>
                    ) : (
                      <div
                        className={`px-3 py-2 ${getUrgencyStyles(notification.urgency, notification.read)}`}
                        onClick={() => {
                          if (notification.source === 'server' && !notification.read) {
                            handleMarkRead(notification.id)
                          }
                        }}
                        style={{ cursor: notification.source === 'server' && !notification.read ? 'pointer' : undefined }}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-base flex-shrink-0">{getIcon(notification.type)}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-medium leading-tight ${notification.read ? 'text-gray-500' : 'text-gray-900'}`}>
                              {notification.title}
                            </p>
                            <p className="text-xs text-gray-500">{notification.message}</p>
                          </div>
                          {!notification.read && (
                            <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200">
              <Link
                to="/dashboard"
                onClick={() => setIsOpen(false)}
                className="block text-center text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View all on Dashboard
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
